import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Select,
  SelectItem,
  Textarea,
} from "@heroui/react";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";
import {
  CheckCircle,
  Warning,
  File,
  PencilSimple,
  Upload,
  Trash,
  ArrowRight,
  Question,
} from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import ModalUploadFileLog, {
  FileUploadField,
} from "shared/components/uploadFileModal/uploadFileModal";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Type assertion for React 18 compatibility
const DatePicker = ReactDatePicker as any;

import { FileLogStatus } from "shared/types/file";

interface DocumentLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  logs: any[];
  resource: {
    id: string;
    isBuilding: boolean;
    name: string;
    label: string;
  } | null;
  getUserName: (userId: string) => string;
}

export const DueDiligenceDocumentLogModal: React.FC<DocumentLogModalProps> = ({
  isOpen,
  onClose,
  logs,
  resource,
  getUserName,
}) => {
  const { session } = useSession();
  const [sortedLogs, setSortedLogs] = useState<any[]>([]);
  const [editingLogId, setEditingLogId] = useState<number | null>(null);
  const [editingLog, setEditingLog] = useState<any>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  useEffect(() => {
    if (logs) {
      // Sort logs by creation date (newest first)
      const sorted = [...logs].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      setSortedLogs(sorted);
    }
  }, [logs]);

  if (!logs || !resource) return null;

  const handleEditClick = (log: any) => {
    setEditingLogId(log.id);
    setEditingLog({
      ...log,
      documentDate: log.documentDate ? parseISO(log.documentDate) : null,
      expirationDate: log.expirationDate ? parseISO(log.expirationDate) : null,
    });
  };

  const handleCancelEdit = () => {
    setEditingLogId(null);
    setEditingLog(null);
  };

  const handleSaveEdit = async () => {
    try {
      if (!editingLog) return;

      const updatedData = {
        ...editingLog,
        documentDate: editingLog.documentDate
          ? format(editingLog.documentDate, "yyyy-MM-dd")
          : null,
        expirationDate: editingLog.expirationDate
          ? format(editingLog.expirationDate, "yyyy-MM-dd")
          : null,
      };

      const res = await fetchWithAuth(
        `${API_URL}/file/update-file-log/${editingLogId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(updatedData),
        },
        session,
      );

      if (res.ok) {
        toast.success("Document bijgewerkt");

        // Update the sorted logs state
        setSortedLogs((prev) =>
          prev.map((log) =>
            log.id === editingLogId ? { ...log, ...updatedData } : log,
          ),
        );

        setEditingLogId(null);
        setEditingLog(null);
      } else {
        toast.error("Kon document niet bijwerken");
      }
    } catch (error) {
      console.error("Error updating file:", error);
      toast.error("Fout bij bijwerken van document");
    }
  };

  const handleDeleteLog = async (logId: number) => {
    if (!confirm("Weet je zeker dat je dit document wilt verwijderen?")) return;

    try {
      // Find the log to get its sharePointFileId
      const logToDelete = sortedLogs.find((log) => log.id === logId);
      if (!logToDelete) {
        toast.error("Document niet gevonden");
        return;
      }

      const res = await fetchWithAuth(
        `${API_URL}/file`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: logId,
            sharePointFileId: logToDelete.sharePointFileId,
          }),
        },
        session,
      );

      if (res.ok) {
        toast.success("Document verwijderd");
        // Remove the log from the state
        setSortedLogs((prev) => prev.filter((log) => log.id !== logId));
      } else {
        toast.error("Kon document niet verwijderen");
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error("Fout bij verwijderen van document");
    }
  };

  const handleFileUploadSuccess = (data: any) => {
    toast.success("Document toegevoegd");
    setIsUploadModalOpen(false);

    // Add the new log to the sorted logs state
    if (data && data.fileLog) {
      setSortedLogs((prev) => [data.fileLog, ...prev]);
    }
  };

  const renderRelations = (relations: any[]) => {
    if (!relations || !Array.isArray(relations) || relations.length === 0) {
      return <span className="text-neutral-secondary-mid">-</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {relations.map((relation, idx) => (
          <a
            href={`/${relation.type}/${relation.resourceId}`}
            target="_blank"
            key={idx}
            className="bg-moss-primary/10 text-moss-primary border-moss-primary/30 flex items-center gap-2 rounded-md border p-2 text-xs"
          >
            {relation.name} <ArrowRight size={12} />
          </a>
        ))}
      </div>
    );
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="full"
        scrollBehavior="inside"
        classNames={{
          base: "max-w-[95vw] max-h-[90vh]",
          body: "p-0",
          backdrop: "bg-neutral-primary/50 backdrop-blur-sm",
        }}
      >
        <ModalContent className="border-moss-primary/20 border-2 shadow-2xl">
          <ModalHeader className="from-secondary/60 border-moss-primary/30 flex flex-col gap-3 border-b-2 bg-gradient-to-br to-white px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-moss-primary/10 border-moss-primary/30 rounded-lg border p-2">
                  <File
                    size={28}
                    className="text-moss-primary"
                    weight="duotone"
                  />
                </div>
                <div>
                  <h3 className="text-neutral-primary text-2xl font-bold">
                    {resource.label}
                  </h3>
                  <p className="text-neutral-secondary-mid text-sm font-medium">
                    {resource.isBuilding ? "Gebouw" : "Unit"}: {resource.name}
                  </p>
                </div>
              </div>
              <Button
                size="md"
                className="from-moss-primary to-moss-primary/90 hover:from-moss-primary/90 hover:to-moss-primary bg-gradient-to-br font-semibold text-white shadow-md transition-all hover:scale-105 hover:shadow-lg"
                startContent={<Upload size={18} weight="bold" />}
                onClick={() => setIsUploadModalOpen(true)}
              >
                Voeg document toe
              </Button>
            </div>
          </ModalHeader>

          <ModalBody className="p-4">
            <Table
              aria-label="Document Log History"
              removeWrapper
              classNames={{
                wrapper:
                  "w-full rounded-lg border border-neutral-secondary-mid/40 shadow-sm overflow-hidden",
                table: "w-full border-collapse",
                th: "bg-gradient-to-b from-secondary to-secondary/80 text-neutral-primary border-b-2 border-moss-primary/20 border-r border-neutral-secondary-mid/40 p-3 text-xs font-bold uppercase tracking-wide",
                td: "text-neutral-primary border-b border-r border-neutral-secondary-mid/30 p-3 text-sm",
                tr: "even:bg-white odd:bg-secondary/20 hover:bg-moss-primary/5 transition-colors",
              }}
            >
              <TableHeader>
                <TableColumn>Status</TableColumn>
                <TableColumn>Bestandsnaam</TableColumn>
                <TableColumn>Documentdatum</TableColumn>
                <TableColumn>Samenvatting</TableColumn>
                <TableColumn>Vervaldatum</TableColumn>
                <TableColumn>Geüpload op</TableColumn>
                <TableColumn>Geüpload door</TableColumn>
                <TableColumn>Relaties</TableColumn>
                <TableColumn>Acties</TableColumn>
              </TableHeader>
              <TableBody
                emptyContent={
                  <div className="flex flex-col items-center justify-center gap-3 p-8">
                    <File
                      size={48}
                      className="text-neutral-secondary-mid/50"
                      weight="duotone"
                    />
                    <p className="text-neutral-secondary-mid text-sm font-medium">
                      Geen documentgeschiedenis beschikbaar
                    </p>
                  </div>
                }
              >
                {sortedLogs.map((log, index) => (
                  <TableRow key={`${log.id || index}`}>
                    <TableCell>
                      {editingLogId === log.id ? (
                        <Select
                          selectedKeys={[editingLog.status]}
                          onChange={(e) =>
                            setEditingLog({
                              ...editingLog,
                              status: e.target.value,
                            })
                          }
                          classNames={{
                            trigger: "min-w-[180px]",
                            listbox: "min-w-[180px]",
                          }}
                        >
                          <SelectItem key={FileLogStatus.OK}>
                            Conform
                          </SelectItem>
                          <SelectItem key={FileLogStatus.NOT_REQUIRED}>
                            Niet van toepassing
                          </SelectItem>
                          <SelectItem key={FileLogStatus.OK_WITH_REMARKS}>
                            Conform, maar met opmerkingen
                          </SelectItem>
                          <SelectItem key={FileLogStatus.NOT_OK}>
                            Niet conform
                          </SelectItem>
                          <SelectItem key={FileLogStatus.UNKNOWN}>
                            Onbekend / Na te kijken
                          </SelectItem>
                        </Select>
                      ) : (
                        <>
                          {log.status === FileLogStatus.OK && (
                            <div className="bg-status-good/10 text-status-good border-status-good/30 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5">
                              <CheckCircle size={18} weight="fill" />
                              <span className="text-xs font-semibold">
                                Conform
                              </span>
                            </div>
                          )}
                          {log.status === FileLogStatus.NOT_REQUIRED && (
                            <div className="bg-status-good/10 text-status-good border-status-good/30 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5">
                              <CheckCircle size={18} weight="fill" />
                              <span className="text-xs font-semibold">
                                Niet van toepassing
                              </span>
                            </div>
                          )}
                          {log.status === FileLogStatus.OK_WITH_REMARKS && (
                            <div className="bg-status-warning/10 text-status-warning border-status-warning/30 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5">
                              <CheckCircle size={18} weight="fill" />
                              <span className="text-xs font-semibold">
                                Conform met opmerkingen
                              </span>
                            </div>
                          )}
                          {log.status === FileLogStatus.NOT_OK && (
                            <div className="bg-brick-primary/10 text-brick-primary border-brick-primary/30 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5">
                              <Warning size={18} weight="fill" />
                              <span className="text-xs font-semibold">
                                Niet conform
                              </span>
                            </div>
                          )}
                          {log.status === FileLogStatus.UNKNOWN && (
                            <div className="bg-neutral-secondary-mid/10 text-neutral-secondary-mid border-neutral-secondary-mid/30 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5">
                              <Question size={18} weight="fill" />
                              <span className="text-xs font-semibold">
                                Onbekend
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {log.fileName || `${resource.label} (${index + 1})`}
                    </TableCell>
                    <TableCell>
                      {editingLogId === log.id ? (
                        <DatePicker
                          selected={editingLog.documentDate}
                          onChange={(date: any) =>
                            setEditingLog({ ...editingLog, documentDate: date })
                          }
                          dateFormat="dd/MM/yyyy"
                          className="w-full rounded-md border p-2"
                        />
                      ) : log.documentDate ? (
                        format(new Date(log.documentDate), "dd MMM yyyy", {
                          locale: nl,
                        })
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {editingLogId === log.id ? (
                        <Textarea
                          value={editingLog.summary}
                          onChange={(e: any) =>
                            setEditingLog({
                              ...editingLog,
                              summary: e.target.value,
                            })
                          }
                        />
                      ) : (
                        log.summary || "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {editingLogId === log.id ? (
                        <DatePicker
                          selected={editingLog.expirationDate}
                          onChange={(date: any) =>
                            setEditingLog({
                              ...editingLog,
                              expirationDate: date,
                            })
                          }
                          dateFormat="dd/MM/yyyy"
                          className="w-full rounded-md border p-2"
                        />
                      ) : log.expirationDate ? (
                        format(new Date(log.expirationDate), "dd MMM yyyy", {
                          locale: nl,
                        })
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      {log.createdAt
                        ? format(new Date(log.createdAt), "dd MMM yyyy", {
                            locale: nl,
                          })
                        : "-"}
                    </TableCell>
                    <TableCell>{getUserName(log.createdBy)}</TableCell>
                    <TableCell>{renderRelations(log.relations)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {editingLogId === log.id ? (
                          <>
                            <Button
                              size="sm"
                              className="from-moss-primary to-moss-primary/90 hover:from-moss-primary/90 hover:to-moss-primary bg-gradient-to-br font-semibold text-white shadow-md transition-all hover:shadow-lg"
                              onClick={handleSaveEdit}
                            >
                              Opslaan
                            </Button>
                            <Button
                              size="sm"
                              className="text-neutral-primary border-neutral-secondary-mid hover:bg-secondary border-2 bg-white shadow-sm transition-colors"
                              onClick={handleCancelEdit}
                            >
                              Annuleren
                            </Button>
                          </>
                        ) : (
                          <>
                            {log.sharePointFileUrl && (
                              <Button
                                as="a"
                                href={log.sharePointFileUrl}
                                target="_blank"
                                size="sm"
                                className="from-moss-primary to-moss-primary/90 hover:from-moss-primary/90 hover:to-moss-primary bg-gradient-to-br font-semibold text-white shadow-md transition-all hover:shadow-lg"
                              >
                                Bekijken
                              </Button>
                            )}
                            <Button
                              size="sm"
                              className="text-neutral-primary border-neutral-secondary-mid hover:bg-secondary border-2 bg-white shadow-sm transition-colors"
                              onClick={() => handleEditClick(log)}
                              startContent={
                                <PencilSimple size={16} weight="bold" />
                              }
                            >
                              Bewerken
                            </Button>
                            <Button
                              size="sm"
                              className="from-brick-primary to-brick-primary/90 hover:from-brick-primary/90 hover:to-brick-primary bg-gradient-to-br font-semibold text-white shadow-md transition-all hover:shadow-lg"
                              onClick={() => handleDeleteLog(log.id)}
                              startContent={<Trash size={16} weight="bold" />}
                            >
                              Verwijderen
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ModalBody>

          <ModalFooter className="border-moss-primary/20 to-secondary/40 border-t-2 bg-gradient-to-br from-white px-6 py-4">
            <Button
              className="text-neutral-primary border-neutral-secondary-mid hover:bg-secondary border-2 bg-white px-6 font-semibold shadow-sm transition-colors"
              onPress={onClose}
            >
              Sluiten
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Upload Modal */}
      {isUploadModalOpen && resource && (
        <ModalUploadFileLog
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onFileUploadSuccess={handleFileUploadSuccess}
          building={resource.isBuilding ? { id: resource.id } : null}
          unit={!resource.isBuilding ? { id: resource.id } : null}
          skipFields={
            resource.isBuilding
              ? [FileUploadField.UNIT]
              : [FileUploadField.BUILDING]
          }
          defaultFileLabel={resource.label as any}
        />
      )}
    </>
  );
};
